import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { API, errorText } from '../governance_v2/constants';
import { MODULE_LABELS } from '../iad/constants';
import { SELECT_CLS } from './constants';

const L = ({ children }) => <span className="mb-1 block text-xs font-medium text-slate-500">{children}</span>;
const EMPTY_PRED = { kpi_name: '', module_type: '', expected_value: '', expected_date: '' };

export const useKpiOptions = (companyId) => useQuery({
  queryKey: ['kpi-options', companyId],
  queryFn: async () => (await axios.get(`${API}/companies/${companyId}/projects-kpi-options`)).data.items,
  enabled: !!companyId, staleTime: 300000,
});

export const PredictionFields = ({ companyId, value, onChange, idx }) => {
  const { data: kpis = [] } = useKpiOptions(companyId);
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  const pick = (e) => {
    const k = kpis[Number(e.target.value)];
    if (k) onChange({ ...value, kpi_name: k.kpi_name, module_type: k.module_type || '' });
  };
  return (
    <div className="grid grid-cols-4 gap-2">
      <label className="col-span-2"><L>KPI afectado</L>
        <select className={SELECT_CLS} value={kpis.findIndex((k) => k.kpi_name === value.kpi_name && (k.module_type || '') === value.module_type)} onChange={pick} data-testid={`prediction-kpi-${idx}`}>
          <option value={-1}>{value.kpi_name || 'Elegir KPI de las ingestas…'}</option>
          {kpis.map((k, i) => <option key={`${k.kpi_name}-${k.module_type}`} value={i}>{k.kpi_name} · {MODULE_LABELS[k.module_type] || k.module_type} (actual {Number(k.latest_value).toFixed(2)})</option>)}
        </select></label>
      <label><L>Valor esperado</L><Input type="number" value={value.expected_value} onChange={set('expected_value')} data-testid={`prediction-expected-${idx}`} /></label>
      <label><L>Fecha esperada</L><Input type="date" value={value.expected_date} onChange={set('expected_date')} data-testid={`prediction-date-${idx}`} /></label>
      <label className="col-span-4"><L>…o escribe el nombre del KPI</L><Input value={value.kpi_name} onChange={set('kpi_name')} placeholder="Ej. Margen Bruto Pct" data-testid={`prediction-kpi-text-${idx}`} /></label>
    </div>
  );
};

export const cleanPrediction = (p) => ({ kpi_name: p.kpi_name.trim(), module_type: p.module_type || null,
  expected_value: Number(p.expected_value), expected_date: p.expected_date || null });

const ProjectFormDialog = ({ companyId, onClose, onCreated }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ title: '', description: '', start_date: '', end_date: '', module_type: '' });
  const [preds, setPreds] = useState([]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => axios.post(`${API}/companies/${companyId}/projects`, {
      ...f, start_date: f.start_date || null, end_date: f.end_date || null, module_type: f.module_type || null,
      predictions: preds.filter((p) => p.kpi_name.trim() && p.expected_value !== '').map(cleanPrediction) }),
    onSuccess: (r) => { toast.success('Proyecto creado'); qc.invalidateQueries({ queryKey: ['projects', companyId] }); qc.invalidateQueries({ queryKey: ['portfolio', companyId] }); onCreated?.(r.data.project.id); onClose(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="project-form-dialog">
        <DialogHeader><DialogTitle>Nuevo proyecto</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <label className="col-span-3"><L>Título</L><Input value={f.title} onChange={set('title')} data-testid="project-form-title" /></label>
          <label className="col-span-3"><L>Descripción</L><Textarea rows={2} value={f.description} onChange={set('description')} data-testid="project-form-description" /></label>
          <label><L>Inicio</L><Input type="date" value={f.start_date} onChange={set('start_date')} data-testid="project-form-start" /></label>
          <label><L>Fin</L><Input type="date" value={f.end_date} onChange={set('end_date')} data-testid="project-form-end" /></label>
          <label><L>Módulo</L><select className={SELECT_CLS} value={f.module_type} onChange={set('module_type')} data-testid="project-form-module">
            <option value="">Corporativo</option>{Object.entries(MODULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        </div>
        <div className="space-y-3 border-t border-slate-200 pt-3 dark:border-slate-700">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold">Predicciones (Predicción vs Realidad)</p>
            <Button size="sm" variant="outline" onClick={() => setPreds([...preds, { ...EMPTY_PRED }])} data-testid="project-form-add-prediction"><Plus className="mr-1 h-3.5 w-3.5" />Agregar KPI</Button>
          </div>
          {preds.map((p, i) => (
            <div key={i} className="relative rounded-lg border border-slate-200 p-3 dark:border-slate-700">
              <PredictionFields companyId={companyId} idx={i} value={p} onChange={(v) => setPreds(preds.map((x, j) => (j === i ? v : x)))} />
              <button type="button" className="absolute right-2 top-2 text-slate-400 hover:text-red-500" onClick={() => setPreds(preds.filter((_, j) => j !== i))} data-testid={`project-form-remove-prediction-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {!preds.length && <p className="text-xs text-slate-500">Opcional. La línea base se toma del último valor del KPI en las ingestas.</p>}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="project-form-cancel">Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || f.title.trim().length < 3} data-testid="project-form-save">{save.isPending ? 'Creando…' : 'Crear proyecto'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectFormDialog;
